import React, { useMemo, useRef, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import EsriGenericMap from "src/components/Maps/EsriGenericMap";

/* =====================================================================================================================
 * Styling constants
 * =================================================================================================================== */

/**
 * RGBA color used to draw past legs (solid).
 */
const COLOR_PAST = [40, 40, 40, 255];

/**
 * RGBA color used to draw future legs (dashed).
 */
const COLOR_FUTURE = [40, 40, 40, 190];

/**
 * RGBA highlight color used to draw the current leg (gold).
 */
const COLOR_CURRENT = [212, 175, 55, 255];

/**
 * Line widths expressed in pixels for ArcGIS line symbols.
 */
const WIDTH_PAST = 2;
const WIDTH_CURRENT = 4;
const WIDTH_FUTURE = 2;

/**
 * Pin colors used for semantic endpoints.
 */
const PIN_COLOR_ORIGIN = [20, 120, 60, 255];
const PIN_COLOR_DESTINATION = [30, 80, 200, 255];
const PIN_COLOR_TRANSSHIP = [120, 70, 170, 255];

/**
 * Common outline color for pins to improve contrast on basemaps.
 */
const PIN_OUTLINE_COLOR = [255, 255, 255, 255];

/**
 * SVG path used for pin markers (ArcGIS "simple-marker" with style "path").
 */
const PIN_PATH =
    "M16 0C9.4 0 4 5.4 4 12c0 9.4 12 20 12 20s12-10.6 12-20C28 5.4 22.6 0 16 0zM16 17.2c-2.9 0-5.2-2.3-5.2-5.2S13.1 6.8 16 6.8s5.2 2.3 5.2 5.2-2.3 5.2-5.2 5.2z";

/* =====================================================================================================================
 * Numeric helpers
 * =================================================================================================================== */

/**
 * Attempts to convert a value into a finite number.
 *
 * @param {any} v Input value
 * @returns {number|null} Finite number or null when invalid
 */
const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

/**
 * Rounds a number to 6 decimals.
 *
 * Purpose:
 * - Stabilizes coordinate values.
 * - Avoids tiny floating point differences triggering unnecessary re-renders.
 *
 * @param {any} n Input number
 * @returns {any} Rounded number or the original value when not finite
 */
const round6 = (n) => (Number.isFinite(n) ? Math.round(n * 1_000_000) / 1_000_000 : n);

/* =====================================================================================================================
 * Geometry helpers
 * =================================================================================================================== */

/**
 * Builds an arrow representation between two coordinates.
 *
 * Output:
 * - shaft: polyline ending slightly before the destination
 * - head: triangular polygon at the destination
 *
 * IMPORTANT:
 * - This arrow should be used as a directional hint only.
 * - The route leg itself must be rendered as a polyline to preserve the real travel geometry.
 *
 * @param {{lon:number,lat:number}} src Source coordinate
 * @param {{lon:number,lat:number}} dst Destination coordinate
 * @returns {{shaft:object, head:object}|null} Arrow geometry payload or null when invalid
 */
function makeArrowJsonArrow(src, dst) {
    if (!src || !dst) return null;

    const aLon = toNum(src.lon);
    const aLat = toNum(src.lat);
    const bLon = toNum(dst.lon);
    const bLat = toNum(dst.lat);
    if (![aLon, aLat, bLon, bLat].every(Number.isFinite)) return null;

    const dx = bLon - aLon;
    const dy = bLat - aLat;
    const len = Math.hypot(dx, dy);
    if (!Number.isFinite(len) || len < 1e-8) return null;

    const ux = dx / len;
    const uy = dy / len;

    const headLen = Math.max(0.25, Math.min(1.2, len * 0.1));
    const headWidth = headLen * 0.9;

    const baseLon = bLon - ux * headLen;
    const baseLat = bLat - uy * headLen;

    const px = -uy;
    const py = ux;

    const leftLon = baseLon + px * headWidth * 0.5;
    const leftLat = baseLat + py * headWidth * 0.5;
    const rightLon = baseLon - px * headWidth * 0.5;
    const rightLat = baseLat - py * headWidth * 0.5;

    return {
        shaft: {
            type: "polyline",
            epsg: 4326,
            graphics: [
                {
                    geometry: [[[aLon, aLat], [baseLon, baseLat]]],
                    attributes: { id: "tracking-current-shaft" },
                },
            ],
        },
        head: {
            type: "polygon",
            epsg: 4326,
            graphics: [
                {
                    geometry: [
                        [
                            [leftLon, leftLat],
                            [bLon, bLat],
                            [rightLon, rightLat],
                            [leftLon, leftLat],
                        ],
                    ],
                    attributes: { id: "tracking-current-head" },
                },
            ],
        },
    };
}

/**
 * Computes the squared distance between two lon/lat points.
 *
 * @param {{lon:number,lat:number}} a Point A
 * @param {{lon:number,lat:number}} b Point B
 * @returns {number} Squared distance (lower is closer)
 */
function dist2(a, b) {
    const dx = Number(a.lon) - Number(b.lon);
    const dy = Number(a.lat) - Number(b.lat);
    return dx * dx + dy * dy;
}

/* =====================================================================================================================
 * Tracking payload extractors
 * =================================================================================================================== */

/**
 * Attempts to pick the route GeoJSON structure from multiple expected shapes.
 *
 * @param {object} tracking Tracking DTO
 * @returns {object|null} GeoJSON object or null when missing
 */
function pickRouteGeoJson(tracking) {
    return (
        tracking?.RouteGeoJson ??
        tracking?.route_geojson ??
        tracking?.Metadata?.route_geojson ??
        tracking?.metadata?.route_geojson ??
        null
    );
}

/**
 * Attempts to pick the current position from multiple expected shapes.
 *
 * Supported shapes:
 * - { lon, lat } / { longitude, latitude } / { x, y }
 * - [lon, lat]
 *
 * IMPORTANT:
 * - Some providers populate a "current_position" that is not a true live position (example: origin snapshot).
 * - This component treats the value as a hint only.
 *
 * @param {object} tracking Tracking DTO
 * @returns {{lon:number,lat:number}|null} Position or null when invalid
 */
function pickCurrentPosition(tracking) {
    const p =
        tracking?.CurrentPosition ??
        tracking?.current_position ??
        tracking?.Metadata?.current_position ??
        tracking?.metadata?.current_position ??
        null;

    if (Array.isArray(p) && p.length >= 2) {
        const lon = toNum(p[0]);
        const lat = toNum(p[1]);
        if (Number.isFinite(lon) && Number.isFinite(lat)) {
            return { lon: round6(lon), lat: round6(lat) };
        }
    }

    const lon = toNum(p?.lon ?? p?.Lon ?? p?.longitude ?? p?.Longitude ?? p?.x);
    const lat = toNum(p?.lat ?? p?.Lat ?? p?.latitude ?? p?.Latitude ?? p?.y);

    if (Number.isFinite(lon) && Number.isFinite(lat)) {
        return { lon: round6(lon), lat: round6(lat) };
    }

    return null;
}

/**
 * Attempts to pick a destination position from multiple expected shapes.
 *
 * @param {object} tracking Tracking DTO
 * @returns {{lon:number,lat:number}|null} Destination coordinate or null
 */
function pickDestinationPosition(tracking) {
    const p =
        tracking?.Destination?.Position ??
        tracking?.destination_position ??
        tracking?.Metadata?.destination_position ??
        tracking?.metadata?.destination_position ??
        tracking?.Metadata?.destination ??
        tracking?.metadata?.destination ??
        null;

    const lon = toNum(p?.lon ?? p?.longitude ?? p?.Longitude ?? p?.x);
    const lat = toNum(p?.lat ?? p?.latitude ?? p?.Latitude ?? p?.y);

    if (Number.isFinite(lon) && Number.isFinite(lat)) {
        return { lon: round6(lon), lat: round6(lat) };
    }

    return null;
}

/* =====================================================================================================================
 * GeoJSON normalization
 * =================================================================================================================== */

/**
 * Extracts ordered route features from a provider "route_geojson" object.
 *
 * Supported inputs:
 * - FeatureCollection with features[] (preferred)
 * - Feature with embedded geometry
 * - Geometry object directly
 *
 * @param {object|null} routeGeoJson GeoJSON input
 * @returns {Array<object>} Ordered feature-like objects
 */
function normalizeRouteFeatures(routeGeoJson) {
    if (!routeGeoJson) return [];

    if (routeGeoJson?.type === "FeatureCollection" && Array.isArray(routeGeoJson.features)) {
        return routeGeoJson.features.filter(Boolean);
    }

    if (routeGeoJson?.type === "Feature") {
        return [routeGeoJson];
    }

    if (routeGeoJson?.type && routeGeoJson?.coordinates) {
        return [{ type: "Feature", geometry: routeGeoJson, properties: {} }];
    }

    return [];
}

/**
 * Extracts ordered Point stops from route features.
 *
 * Returned shape:
 * - { lon, lat, code, name, country, status, index }
 *
 * @param {Array<object>} features Normalized route features
 * @returns {Array<object>} Ordered stop list
 */
function extractStopsFromFeatures(features) {
    const stops = [];

    for (const f of features) {
        const g = f?.geometry;
        if (g?.type !== "Point") continue;

        const coords = g?.coordinates;
        if (!Array.isArray(coords) || coords.length < 2) continue;

        const lon = toNum(coords[0]);
        const lat = toNum(coords[1]);
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;

        const props = f?.properties || {};
        const loc = props?.location || {};

        stops.push({
            lon: round6(lon),
            lat: round6(lat),
            code: String(loc?.code ?? props?.code ?? "").trim() || null,
            name: String(loc?.name ?? props?.name ?? "").trim() || null,
            country: String(loc?.country?.name ?? props?.country ?? "").trim() || null,
            status: String(props?.status ?? "").trim() || null,
        });
    }

    if (!stops.length) return [];

    // Remove consecutive duplicates to avoid "double pins" for the same stop.
    const deduped = [];
    for (const s of stops) {
        const prev = deduped[deduped.length - 1];
        if (!prev) {
            deduped.push(s);
            continue;
        }

        const sameCoord = Number(prev.lon) === Number(s.lon) && Number(prev.lat) === Number(s.lat);
        const sameCode = prev.code && s.code && prev.code === s.code;

        if (sameCoord || sameCode) continue;
        deduped.push(s);
    }

    return deduped.map((s, i) => ({ ...s, index: i + 1 }));
}

/**
 * Extracts ordered LineString legs from route features.
 *
 * Returned shape:
 * - { coords:[[lon,lat],...], status, currentFlag, vesselName, voyage }
 *
 * @param {Array<object>} features Normalized route features
 * @returns {Array<object>} Ordered legs list
 */
function extractLegsFromFeatures(features) {
    const legs = [];

    for (const f of features) {
        const g = f?.geometry;
        if (g?.type !== "LineString" || !Array.isArray(g.coordinates)) continue;

        const coords = g.coordinates
            .map((c) => [toNum(c?.[0]), toNum(c?.[1])])
            .filter((c) => Number.isFinite(c[0]) && Number.isFinite(c[1]))
            .map((c) => [round6(c[0]), round6(c[1])]);

        if (coords.length < 2) continue;

        const props = f?.properties || {};

        legs.push({
            coords,
            status: String(props?.status ?? "").trim() || null,
            currentFlag: props?.current != null ? !!props.current : false,
            vesselName: String(props?.vessel?.name ?? "").trim() || null,
            voyage: String(props?.voyage ?? "").trim() || null,
        });
    }

    return legs;
}

/**
 * Finds the nearest coordinate index on a polyline to a provided position.
 *
 * @param {Array<Array<number>>} coords Polyline coordinates
 * @param {{lon:number,lat:number}|null} pos Current position
 * @returns {number|null} Nearest index or null when not computable
 */
function nearestIndexOnLine(coords, pos) {
    if (!Array.isArray(coords) || coords.length < 2 || !pos) return null;

    let bestIdx = null;
    let bestD = Infinity;

    for (let i = 0; i < coords.length; i++) {
        const c = coords[i];
        const lon = toNum(c?.[0]);
        const lat = toNum(c?.[1]);
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;

        const d = dist2({ lon, lat }, pos);
        if (d < bestD) {
            bestD = d;
            bestIdx = i;
        }
    }

    return bestIdx;
}

/**
 * Selects the current leg index from legs, using a deterministic priority strategy.
 *
 * Priority:
 * 1) A leg with explicit currentFlag=true
 * 2) A leg whose status is CURRENT/ACTIVE/IN_TRANSIT (case-insensitive)
 * 3) A leg nearest to currentPos (by nearest polyline point)
 * 4) The last leg when none of the above is available
 *
 * @param {Array<object>} legs Extracted leg list
 * @param {{lon:number,lat:number}|null} currentPos Optional current position
 * @returns {number|null} Index of the chosen current leg
 */
function pickCurrentLegIndex(legs, currentPos) {
    if (!Array.isArray(legs) || legs.length === 0) return null;

    const idxByFlag = legs.findIndex((l) => l?.currentFlag === true);
    if (idxByFlag >= 0) return idxByFlag;

    const idxByStatus = legs.findIndex((l) => {
        const s = String(l?.status ?? "").trim().toLowerCase();
        return s === "current" || s === "active" || s === "in_transit";
    });
    if (idxByStatus >= 0) return idxByStatus;

    if (currentPos) {
        let bestLeg = null;
        let bestD = Infinity;

        for (let i = 0; i < legs.length; i++) {
            const coords = legs[i]?.coords;
            if (!Array.isArray(coords) || coords.length < 2) continue;

            const nearestIdx = nearestIndexOnLine(coords, currentPos);
            if (nearestIdx == null) continue;

            const p = coords[nearestIdx];
            const d = dist2({ lon: p[0], lat: p[1] }, currentPos);

            if (d < bestD) {
                bestD = d;
                bestLeg = i;
            }
        }

        if (bestLeg != null) return bestLeg;
    }

    return legs.length - 1;
}

/**
 * Splits legs into past/current/future arrays based on a selected current leg index.
 *
 * @param {Array<object>} legs Extracted leg list
 * @param {number|null} currentIdx Selected current leg index
 * @returns {{past:Array<object>, current:object|null, future:Array<object>}} Split legs
 */
function splitLegs(legs, currentIdx) {
    if (!Array.isArray(legs) || legs.length === 0) {
        return { past: [], current: null, future: [] };
    }

    if (currentIdx == null || currentIdx < 0 || currentIdx >= legs.length) {
        return { past: legs, current: null, future: [] };
    }

    const past = legs.slice(0, currentIdx);
    const current = legs[currentIdx] || null;
    const future = legs.slice(currentIdx + 1);

    return { past, current, future };
}

/* =====================================================================================================================
 * Completion detection
 * =================================================================================================================== */

/**
 * Determines whether a shipment should be considered completed for rendering purposes.
 *
 * Purpose:
 * - When completed, all legs must be rendered as PAST and no CURRENT/FUTURE highlight should remain.
 * - Avoids selecting an incorrect "current" leg based on stale provider current_position snapshots.
 *
 * @param {object} tracking Tracking DTO
 * @returns {boolean} True when completed signals are present
 */
function isTrackingCompleted(tracking) {
    const status = String(tracking?.Status ?? "").trim().toLowerCase();
    const currentCode = String(tracking?.CurrentLocation?.Code ?? "").trim().toLowerCase();
    const destCode = String(tracking?.Destination?.Code ?? "").trim().toLowerCase();

    const shipmentStatus = String(tracking?.Metadata?.shipment_status ?? "").trim().toLowerCase();
    const transitPct = toNum(tracking?.Metadata?.transit_percentage);

    // Percentage-based completion (provider-specific but reliable when present).
    if (Number.isFinite(transitPct) && transitPct >= 100) return true;

    // Explicit terminal statuses (expand as your domain taxonomy evolves).
    const terminal = new Set(["delivered", "discharged", "empty_return", "returned", "completed", "complete"]);

    if (terminal.has(status)) return true;
    if (terminal.has(shipmentStatus)) return true;

    // When the current location matches the destination, treat the tracking as complete.
    if (currentCode && destCode && currentCode === destCode) return true;

    return false;
}

/* =====================================================================================================================
 * TrackingLiveMap
 * =================================================================================================================== */

/**
 * TrackingLiveMap
 *
 * Renders a read-only tracking visualization on top of EsriGenericMap.
 *
 * IMPORTANT (per ESRI component docs):
 * - `mapAction("zoomToCoordinates", { coordinates: [lon, lat], epsg })` is supported.
 * - There is NO documented "zoomToExtent" map action in this component.
 *   Therefore, focusing MUST be done via zoomToCoordinates (and/or by changing mapConfig center/zoom).
 */
export default function TrackingLiveMap({ tracking }) {
    const { i18n, t } = useTranslation();
    const mapRef = useRef(null);

    /**
     * Incremented when the ESRI component API becomes available (onComponentReady).
     *
     * Purpose:
     * - Guarantees that the focus effect runs only after the map API exists.
     */
    const [mapReadyTick, setMapReadyTick] = useState(0);

    /**
     * Remembers the last focus signature applied to the map.
     *
     * Purpose:
     * - Avoids re-running zoom on every render/update (map fighting user).
     */
    const focusKeyRef = useRef(null);

    /**
     * Maps i18n language into an ESRI locale string.
     */
    const mapLocale = useMemo(() => {
        const lng = String(i18n.language || "pt").toLowerCase();
        if (lng.startsWith("pt")) return "pt-PT";
        if (lng.startsWith("es")) return "es-ES";
        return "en-US";
    }, [i18n.language]);

    const [mapConfig, setMapConfig] = useState({
        center: [-9.14, 38.72],
        zoom: 3
    });

    /**
     * Extracts route GeoJSON and optional positions.
     */
    const routeGeoJson = useMemo(() => pickRouteGeoJson(tracking), [tracking]);
    const currentPosRaw = useMemo(() => pickCurrentPosition(tracking), [tracking]);
    const destPosRaw = useMemo(() => pickDestinationPosition(tracking), [tracking]);

    /**
     * Normalizes features, then derives ordered stops and legs.
     */
    const routeModel = useMemo(() => {
        const features = normalizeRouteFeatures(routeGeoJson);
        const stops = extractStopsFromFeatures(features);
        const legs = extractLegsFromFeatures(features);
        return { features, stops, legs };
    }, [routeGeoJson]);

    /**
     * Determines whether the shipment should be treated as completed for rendering.
     */
    const completed = useMemo(() => isTrackingCompleted(tracking), [tracking]);

    /**
     * Derives a reliable destination position.
     *
     * Priority:
     * 1) Last stop from route Point features
     * 2) Explicit destination position on tracking payload
     * 3) Last coordinate of the last leg
     */
    const destPos = useMemo(() => {
        const stops = routeModel?.stops || [];
        if (stops.length > 0) {
            const last = stops[stops.length - 1];
            if (Number.isFinite(last?.lon) && Number.isFinite(last?.lat)) return { lon: last.lon, lat: last.lat };
        }

        if (destPosRaw) return destPosRaw;

        const legs = routeModel?.legs || [];
        const lastLeg = legs[legs.length - 1];
        const lastCoord = lastLeg?.coords?.[lastLeg.coords.length - 1];
        if (Array.isArray(lastCoord) && lastCoord.length >= 2) {
            const lon = toNum(lastCoord[0]);
            const lat = toNum(lastCoord[1]);
            if (Number.isFinite(lon) && Number.isFinite(lat)) return { lon: round6(lon), lat: round6(lat) };
        }

        return null;
    }, [routeModel, destPosRaw]);

    /**
     * Derives a reliable current position hint.
     *
     * IMPORTANT:
     * - When completed, keep currentPos aligned with destination to avoid stale origin snapshots.
     */
    const currentPos = useMemo(() => {
        if (completed && destPos) return destPos;
        if (currentPosRaw) return currentPosRaw;
        if (destPos) return destPos;
        return null;
    }, [completed, currentPosRaw, destPos]);

    /**
     * Splits legs into past/current/future buckets.
     *
     * IMPORTANT:
     * - When completed, everything is PAST and no CURRENT/FUTURE bucket should be rendered.
     */
    const legBuckets = useMemo(() => {
        const legs = routeModel?.legs || [];
        if (!legs.length) return { past: [], current: null, future: [] };

        if (completed) return { past: legs, current: null, future: [] };

        const currentIdx = pickCurrentLegIndex(legs, currentPos);
        return splitLegs(legs, currentIdx);
    }, [routeModel, currentPos, completed]);

    /**
     * Builds GeoJSON layers for stop pins.
     */
    const stopPinLayers = useMemo(() => {
        const layers = [];
        const stops = routeModel?.stops || [];
        if (!stops.length) return layers;

        const origin = stops[0];
        const destination = stops[stops.length - 1];
        const transship = stops.slice(1, Math.max(1, stops.length - 1));

        const toFeatureCollection = (list, kind) => ({
            type: "FeatureCollection",
            features: (list || []).map((s) => ({
                type: "Feature",
                geometry: { type: "Point", coordinates: [s.lon, s.lat] },
                properties: {
                    id: `stop-${kind}-${s.index}`,
                    kind,
                    order: s.index,
                    code: s.code,
                    name: s.name,
                    country: s.country,
                    status: s.status,
                    lon: s.lon,
                    lat: s.lat,
                },
            })),
        });

        const pushPinLayer = (title, geojson, color) => {
            const featureCount = geojson?.features?.length ?? 0;
            if (featureCount <= 0) return;

            layers.push({
                title,
                type: "geojson",
                geojson,
                symbol: {
                    type: "simple-marker",
                    style: "path",
                    path: PIN_PATH,
                    size: 26,
                    color,
                    outline: { type: "simple-line", width: 1.8, color: PIN_OUTLINE_COLOR },
                },
            });
        };

        pushPinLayer("Tracking Stop (Origin)", toFeatureCollection([origin], "origin"), PIN_COLOR_ORIGIN);

        if (transship.length > 0) {
            pushPinLayer(
                "Tracking Stop (Transshipment)",
                toFeatureCollection(transship, "transshipment"),
                PIN_COLOR_TRANSSHIP
            );
        }

        pushPinLayer(
            "Tracking Stop (Destination)",
            toFeatureCollection([destination], "destination"),
            PIN_COLOR_DESTINATION
        );

        return layers;
    }, [routeModel]);

    /**
     * Builds line and arrow layers for route legs.
     */
    const routeLineLayers = useMemo(() => {
        const layers = [];

        const pushLegPolyline = (title, coords, symbol, urlSig) => {
            if (!Array.isArray(coords) || coords.length < 2) return;

            layers.push({
                title,
                type: "json",
                url: urlSig,
                json: {
                    type: "polyline",
                    epsg: 4326,
                    graphics: [{ geometry: [coords], attributes: { id: urlSig } }],
                },
                symbol,
            });
        };

        for (let i = 0; i < (legBuckets?.past?.length ?? 0); i++) {
            const leg = legBuckets.past[i];
            const sig = `trk:past:${i}:${leg?.coords?.length ?? 0}`;
            pushLegPolyline("Tracking Route (Past)", leg.coords, { type: "simple-line", color: COLOR_PAST, width: WIDTH_PAST }, sig);
        }

        for (let i = 0; i < (legBuckets?.future?.length ?? 0); i++) {
            const leg = legBuckets.future[i];
            const sig = `trk:future:${i}:${leg?.coords?.length ?? 0}`;
            pushLegPolyline(
                "Tracking Route (Future)",
                leg.coords,
                { type: "simple-line", color: COLOR_FUTURE, width: WIDTH_FUTURE, style: "dash" },
                sig
            );
        }

        const cur = legBuckets?.current;
        if (cur?.coords?.length >= 2) {
            // 1) Render the full current leg polyline (gold).
            pushLegPolyline(
                "Tracking Route (Current)",
                cur.coords,
                { type: "simple-line", color: COLOR_CURRENT, width: WIDTH_CURRENT },
                `trk:current:line:${cur?.coords?.length ?? 0}`
            );

            // 2) Render a directional arrow at the end of the current leg (using last 2 points).
            const n = cur.coords.length;
            const pPrev = cur.coords[Math.max(0, n - 2)];
            const pLast = cur.coords[n - 1];

            const src = { lon: toNum(pPrev?.[0]), lat: toNum(pPrev?.[1]) };
            const dst = { lon: toNum(pLast?.[0]), lat: toNum(pLast?.[1]) };

            const arrow = makeArrowJsonArrow(src, dst);

            if (arrow?.shaft) {
                layers.push({
                    title: "Tracking Route (Current)",
                    type: "json",
                    url: `trk:current:arrow:shaft:${src.lon},${src.lat}->${dst.lon},${dst.lat}`,
                    json: arrow.shaft,
                    symbol: { type: "simple-line", color: COLOR_CURRENT, width: WIDTH_CURRENT },
                });
            }

            if (arrow?.head) {
                layers.push({
                    title: "Tracking Route (Current)",
                    type: "json",
                    url: `trk:current:arrow:head:${src.lon},${src.lat}->${dst.lon},${dst.lat}`,
                    json: arrow.head,
                    symbol: {
                        type: "simple-fill",
                        color: COLOR_CURRENT,
                        outline: { type: "simple-line", color: COLOR_CURRENT, width: 0 },
                    },
                });
            }
        }

        return layers;
    }, [legBuckets]);

    /**
     * Builds optional position layers for current and destination.
     */
    const positionLayers = useMemo(() => {
        const layers = [];

        if (currentPos) {
            layers.push({
                title: "Tracking Position (Current)",
                type: "geojson",
                geojson: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            geometry: { type: "Point", coordinates: [currentPos.lon, currentPos.lat] },
                            properties: {
                                id: "tracking-current-position",
                                lon: currentPos.lon,
                                lat: currentPos.lat,
                            },
                        },
                    ],
                },
                symbol: {
                    type: "simple-marker",
                    style: "circle",
                    size: 10,
                    color: COLOR_CURRENT,
                    outline: { type: "simple-line", width: 2, color: PIN_OUTLINE_COLOR },
                },
            });
        }

        if (destPos) {
            layers.push({
                title: "Tracking Position (Destination)",
                type: "geojson",
                geojson: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            geometry: { type: "Point", coordinates: [destPos.lon, destPos.lat] },
                            properties: {
                                id: "tracking-destination-position",
                                lon: destPos.lon,
                                lat: destPos.lat,
                            },
                        },
                    ],
                },
                symbol: {
                    type: "simple-marker",
                    style: "circle",
                    size: 9,
                    color: PIN_COLOR_DESTINATION,
                    outline: { type: "simple-line", width: 2, color: PIN_OUTLINE_COLOR },
                },
            });
        }

        return layers;
    }, [currentPos, destPos]);

    /**
     * Final layer list.
     */
    const mapLayers = useMemo(() => [...routeLineLayers, ...stopPinLayers, ...positionLayers], [
        routeLineLayers,
        stopPinLayers,
        positionLayers,
    ]);

    /**
     * Stores the EsriGenericMap component API reference for imperative actions.
     *
     * IMPORTANT:
     * - We also tick mapReadyTick so the focus effect runs AFTER the component is actually ready.
     */
    const handleMapReady = useCallback((c) => {
        mapRef.current = c;
        setMapReadyTick((x) => x + 1);
    }, []);

    /**
     * Focus strategy (single source of truth)
     *
     * Rules:
     * - Completed: focus destination
     * - Not completed: focus current position when available, otherwise destination
     * - Fallback: focus origin (first stop) if nothing else exists
     *
     * IMPORTANT (per docs):
     * - Only use mapAction("zoomToCoordinates", { coordinates, epsg })
     * - Always pass epsg: 4326 because our coordinates are lon/lat WGS84.
     */
    useEffect(() => {
        const api = mapRef.current;
        if (!api || typeof api?.mapAction !== "function") return;

        // If the map isn't ready yet, do nothing.
        if (!mapReadyTick) return;

        // Stable signature: apply focus once per "tracking identity + relevant coords + completed"
        const shipmentKey =
            String(
                tracking?.Metadata?.shipment_id ??
                tracking?.ExternalId ??
                tracking?.ProviderReference ??
                tracking?.InternalReference ??
                ""
            ) || "unknown";

        const cp = currentPos ? `${currentPos.lon},${currentPos.lat}` : "null";
        const dp = destPos ? `${destPos.lon},${destPos.lat}` : "null";
        const focusKey = `${shipmentKey}|completed=${completed}|cp=${cp}|dp=${dp}`;

        if (focusKeyRef.current === focusKey) return;

        // Choose focus point
        const stops = routeModel?.stops || [];
        const originPos = stops.length ? { lon: stops[0].lon, lat: stops[0].lat } : null;

        const focusPos = (completed ? destPos : currentPos || destPos) || originPos;
        if (!focusPos) return;

        try {
            focusKeyRef.current = focusKey;

            api.mapAction("zoomToCoordinates", {
                coordinates: [focusPos.lon, focusPos.lat],
                epsg: 4326,
            });

            setMapConfig({
                center: [focusPos.lon, focusPos.lat],
                zoom: 4
            });
        } catch {
            // Best-effort: allow retry on next tick/update
            focusKeyRef.current = null;
        }
    }, [mapReadyTick, tracking, routeModel, currentPos, destPos, completed]);

    /**
     * Legend labels resolved through i18n.
     */
    const legendTitle = t("Legend");
    const legendOrigin = t("Origin");
    const legendTransshipment = t("Transshipment");
    const legendDestination = t("Destination");
    const legendPast = t("Past");
    const legendCurrent = t("Current");
    const legendFuture = t("Future");

    /**
     * Converts RGBA arrays into CSS rgb() strings (alpha intentionally ignored for legend chips).
     *
     * @param {number[]} rgba RGBA array
     * @returns {string} CSS rgb(...) string
     */
    const rgbCss = (rgba) => `rgb(${rgba[0]}, ${rgba[1]}, ${rgba[2]})`;

    return (
        <div style={{ position: "relative", height: "100%", width: "100%" }}>
            <EsriGenericMap
                mode="view"
                locale={mapLocale}
                widgets={{ legend: { position: "bottom-right" } }}
                layers={mapLayers}
                center={mapConfig.center}
                zoom={mapConfig.zoom}
                constraints={{ minZoom: 2 }}
                onComponentReady={handleMapReady}
                showLocations={false}
                locations={[]}
                enableLocationPick={false}
                configDebounceMs={240}
            />

            {/* Custom legend overlay to guarantee clarity regardless of widget legend behavior. */}
            <div
                style={{
                    position: "absolute",
                    right: 12,
                    bottom: 12,
                    zIndex: 6,
                    background: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 10,
                    padding: "10px 12px",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
                    backdropFilter: "blur(2px)",
                    maxWidth: 280,
                    pointerEvents: "none",
                }}
                aria-label={legendTitle}
            >
                <div
                    style={{
                        fontWeight: 700,
                        fontSize: 12,
                        marginBottom: 8,
                        color: "var(--color-red-dark)",
                        textAlign: "right",
                    }}
                >
                    {legendTitle}
                </div>

                <div style={{ display: "grid", gap: 8, fontSize: 12, textAlign: "right" }}>
                    <div style={{ display: "flex", flexDirection: "row-reverse", alignItems: "center", gap: 10 }}>
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: 999,
                                background: rgbCss(PIN_COLOR_ORIGIN),
                                border: "2px solid white",
                            }}
                        />
                        <span>{legendOrigin}</span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "row-reverse", alignItems: "center", gap: 10 }}>
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: 999,
                                background: rgbCss(PIN_COLOR_TRANSSHIP),
                                border: "2px solid white",
                            }}
                        />
                        <span>{legendTransshipment}</span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "row-reverse", alignItems: "center", gap: 10 }}>
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: 999,
                                background: rgbCss(PIN_COLOR_DESTINATION),
                                border: "2px solid white",
                            }}
                        />
                        <span>{legendDestination}</span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "row-reverse", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 22, height: 0, borderTop: `2px solid ${rgbCss(COLOR_PAST)}` }} />
                        <span>{legendPast}</span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "row-reverse", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 22, height: 0, borderTop: `2px solid ${rgbCss(COLOR_CURRENT)}` }} />
                        <span>{legendCurrent}</span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "row-reverse", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 22, height: 0, borderTop: `2px dashed ${rgbCss(COLOR_FUTURE)}` }} />
                        <span>{legendFuture}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

TrackingLiveMap.propTypes = {
    tracking: PropTypes.object,
};